import React, { useState, useEffect } from "react";
import { Card, ProgressBar, Icon, Dialog } from "@blueprintjs/core";
import axios from "axios";
import Flexbox from "flexbox-react";
import ScrollableDropdown from "../shared/ScrollableDropdown";
import { FilledButton, OutlinedButton } from "../buttons-20250224T111359Z-001/buttons";
import Pagination from "react-js-pagination";
import Cookies from "js-cookie";
import ToastStore from "../../stores/ToastStore";
import EmptyStateNotice from "../goal_performance/EmptyStateNotice";
import "../onboarding_settings/OnboardingSettingsView.css";


const EmployeePipelineManager = () => {
    const [search, setSearch] = useState("");
    const [activePage, setActivePage] = useState(1);
    const [perPage] = useState(15);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [allEmployees, setAllEmployees] = useState([]);
    const [checkins, setCheckins] = useState([]);
    const [selectedCheckin, setSelectedCheckin] = useState(null);
    const [openId, setOpenId] = useState(null);
    const [loadingId, setLoadingId] = useState(null);
    const [isAddEmployeeDialogOpen, setIsAddEmployeeDialogOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [checkInEmployees, setCheckInEmployees] = useState([]);
    const [isAllEmployeesSelected, setIsAllEmployeesSelected] = useState(false);


    useEffect(() => {
        fetchAllEmployees();
        fetchCheckins();
    }, []);

    useEffect(() => {
        if (selectedCheckin) {
            setActivePage(1);
            fetchCheckInEmployees(1);
        }
    }, [selectedCheckin]);

    const fetchAllEmployees = async () => {
        const response = await axios.get(`${Cookies.get("apiEnv")}/goals/employee`);
        setAllEmployees(response.data);
    };

    const fetchCheckins = async () => {
        try {
            const response = await axios.get(`${Cookies.get("apiEnv")}/checkins/list`);
            setCheckins(response.data);
            if (response.data.length > 0) {
                setSelectedCheckin(response.data[0]);
            }
        } catch (error) {
            console.error("Error fetching checkins", error);
        }
    };

    const fetchCheckInEmployees = async (page = activePage) => {
        if (!selectedCheckin) return;
        try {
            const response = await axios.get(`${Cookies.get("apiEnv")}/onboarding_users`,
                {
                    params: {
                        checkin_id: selectedCheckin.id,
                        page,
                        per_page: perPage
                    }
                }
            );
            const { data, meta } = response.data;
            const formattedEmployees = data.map((emp) => ({
                id: emp.id,
                user_id: emp.user_id.id,
                email: emp.user_id.email,
                fullName: emp.user_id.fullName,
                imageFileName: emp.user_id.imageFileName,
                role: emp.user_id.job_profile_name,
                completed_tasks: emp.user_id.completed_tasks,
                pending_tasks: emp.user_id.pending_tasks,
                total_tasks: emp.user_id.total_tasks,
                tasks_percent_complete: emp.user_id.tasks_percent_complete,
                status: emp.user_id.status
            }));

            setCheckInEmployees(formattedEmployees);
            setTotalCount(meta.total_count);
            setTotalPages(meta.total_pages);
            setActivePage(meta.page);
        } catch (error) {
            console.error(error);
        }
    };

    const removeUserFromOnboardingPipeline = async (userId) => {
        try {
            await axios.delete(`${Cookies.get("apiEnv")}/onboarding_users`,
                {
                    data: {
                        user_id: userId,
                        checkin_id: selectedCheckin.id
                    }
                }
            );
            ToastStore.showToast("Employee removed from pipeline", "success");

            const isLastItemOnPage = checkInEmployees.length === 1;
            const nextPage = isLastItemOnPage && activePage > 1
                ? activePage - 1
                : activePage;
            fetchCheckInEmployees(nextPage);
        } catch (error) {
            ToastStore.showToast(error?.response?.data?.message || "Failed to remove employee", "danger");
        }
    };

    const handlePageChange = (pageNumber) => {
        fetchCheckInEmployees(pageNumber);
    };

    const handleSelectCheckin = (checkin) => {
        setSelectedCheckin(checkin);
    };

    const addUsersToOnboardingPipeline = async () => {
        try {
            if (selectedEmployees.length === 0) {
                ToastStore.showToast("Please select at least one employee", "danger");
                return;
            }
            const response = await axios.post(`${Cookies.get("apiEnv")}/onboarding_users`, {
                user_ids: selectedEmployees.map((e) => e.id),
                checkin_id: selectedCheckin.id
            });
            fetchCheckInEmployees();
            setIsAddEmployeeDialogOpen(false);
            setSelectedEmployees([]);
            setSelectedEmployee(null);
            ToastStore.showToast("Employee added to pipeline", "success");
        } catch (err) {
            console.error(err);
        }
    };

    const availableEmployees = allEmployees.filter(emp =>
        !selectedEmployees.find(e => e.id === emp.id) &&
        !checkInEmployees.find(e => e.user_id === emp.id)
    );

    return (
        <Flexbox flexGrow={1} flexDirection="column" marginLeft="10px">
            <Flexbox flexDirection="row" alignItems="center" justifyContent="flex-end" className="push-20-b">
                <ScrollableDropdown
                    width={300}
                    label={selectedCheckin?.title || "Select Checkin"}
                    items={checkins}
                    getKey={(e) => e.id}
                    getLabel={(e) => e.title}
                    onSelect={(checkin) => handleSelectCheckin(checkin)}
                />
                <FilledButton text="Add Employees" onClick={() => setIsAddEmployeeDialogOpen(true)} style={{ marginLeft: "10px" }} />
            </Flexbox>
            <Flexbox flexDirection="column"  >

                {/* Employee Cards */}
                {checkInEmployees.length > 0 ? (
                    <>
                        {checkInEmployees.length > 0 && checkInEmployees?.map((emp) => {
                            const statusColor =
                                emp.tasks_percent_complete === 0
                                    ? "#8A919F"
                                    : emp.tasks_percent_complete == 100
                                        ? "#2ECC71"
                                        : "#3B82F6";

                            const initials = emp.fullName?.split(" ").map((n) => n[0]).join("").toUpperCase();

                            return (
                                <Card key={emp.id} className="employee-card" interactive>
                                    <div className="employee-card-header">
                                        {/* Left Section */}
                                        <div className="employee-info">
                                            <div className="employee-avatar">
                                                {emp.imageFileName ? (
                                                    <img src={emp.imageFileName} alt={emp.fullName} />
                                                ) : (
                                                    initials
                                                )}
                                            </div>

                                            <div>
                                                <div className="employee-name">
                                                    {emp.fullName}
                                                    <span className="employee-status push-10-l" style={{ backgroundColor: `${statusColor}20`, color: statusColor }}
                                                    >
                                                        {emp.status}
                                                    </span>
                                                </div>
                                                <div className="employee-role">
                                                    {emp.role || emp.job_profile_name || "Employee"}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right Section */}
                                        <div className="employee-progress-info">
                                            <div className="review-action">
                                                <OutlinedButton text="View"
                                                    onClick={() =>
                                                        window.location.href = `/onboarding/process?emp=${encodeURIComponent(
                                                            btoa(String(emp.user_id))
                                                        )}&checkin=${encodeURIComponent(
                                                            btoa(String(selectedCheckin.id))
                                                        )}`
                                                    }
                                                />
                                                <OutlinedButton
                                                    text="Remove"
                                                    style={{ marginLeft: "8px" }}
                                                    onClick={() =>
                                                        removeUserFromOnboardingPipeline(emp.user_id)
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="employee-progress-bar-container">
                                        <div>
                                            <span className="employee-step push-10-r">Step {emp.completed_tasks}/{emp.total_tasks}</span>
                                        </div>
                                        <div className="employee-progress-bar">
                                            <ProgressBar
                                                value={emp.tasks_percent_complete / 100}
                                                //animate={emp.tasks_percent_complete > 0 && emp.tasks_percent_complete < 100}
                                                stripes={false}
                                                intent={
                                                    emp.tasks_percent_complete === 100
                                                        ? "success"
                                                        : emp.tasks_percent_complete > 0
                                                            ? "primary"
                                                            : "none"
                                                }
                                            />
                                        </div>
                                        <span className="employee-progress-label push-10-l">
                                            {emp.tasks_percent_complete}%
                                        </span>
                                    </div>

                                </Card>
                            );
                        })}</>
                ) : (<EmptyStateNotice title="Add employees to the pipeline" description="Add employees to the pipeline to get started" />)}
            </Flexbox>
            {totalCount > perPage && (
                <Flexbox display="flex" flexDirection="row" className="pagination-wrapper" justifyContent="flex-start" marginTop="20px">
                    <Pagination
                        activePage={activePage}
                        itemsCountPerPage={perPage}
                        totalItemsCount={totalCount}
                        pageRangeDisplayed={5}
                        onChange={handlePageChange}
                        innerClass="pagination"
                        itemClass="page-item"
                        linkClass="page-link"
                    />
                </Flexbox>
            )}
            <Dialog
                portalContainer={document.body}
                style={{ overflow: "visible" }}
                isOpen={isAddEmployeeDialogOpen}
                onClose={() => setIsAddEmployeeDialogOpen(false)}
                title="Add Employee"
            >
                <div style={{ padding: "20px", minWidth: "400px" }}>
                    <p className="bp3-text-muted push-10-b">You are adding employees for the check-in: <strong>{selectedCheckin?.title}</strong></p>

                    <div style={{ position: "relative", zIndex: 9999 }} >
                        <ScrollableDropdown
                            width={300}
                            style={{ overflow: "visible" }}
                            butit will according to t
                            label={
                                isAllEmployeesSelected
                                    ? "All Employees"
                                    : selectedEmployees.length > 0
                                        ? `${selectedEmployees.length} selected`
                                        : "Select Employee"
                            }
                            //label={ selectedEmployees.length > 0 ? `${selectedEmployees.length} selected` : "Select Employee" }
                            items={[{ id: "ALL", first_name: "Select", last_name: "All Employees" }, ...availableEmployees]}
                            getKey={(e) => e.id}
                            getLabel={(e) => e.id === "ALL" ? "Select All Employees" : `${e.first_name} ${e.last_name}`}
                            onSelect={(emp) => {
                                if (emp.id === "ALL") {
                                    setIsAllEmployeesSelected(true);
                                    setSelectedEmployees(availableEmployees);
                                    return;
                                }
                                setIsAllEmployeesSelected(false);
                                const exists = selectedEmployees.find(e => e.id === emp.id);
                                if (!exists) {
                                    setSelectedEmployees([...selectedEmployees, emp]);
                                }
                            }}
                            enableSearch={true}
                        /></div>
                    <div style={{ marginTop: "20px" }}>
                        {isAllEmployeesSelected ? (
                            <div className="employee-selected-item">
                                <strong>All Employees Selected</strong>
                            </div>
                        ) : (
                            <>
                                {selectedEmployees.length > 0 && <strong>Selected Employees:</strong>}
                                {selectedEmployees.map((emp) => (
                                    <div key={emp.id} className="employee-selected-item">
                                        <div className="employee-selected-item-circle">
                                            {emp.first_name[0]}{emp.last_name[0]}
                                        </div>
                                        {emp.first_name} {emp.last_name}
                                        <Icon
                                            icon="cross"
                                            size={12}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => {
                                                setSelectedEmployees(selectedEmployees.filter(e => e.id !== emp.id));
                                                setIsAllEmployeesSelected(false);
                                            }}
                                        />
                                    </div>
                                ))}
                            </>
                        )}
                    </div>


                    {/* Final Submit */}
                    <Flexbox flexDirection="row" justifyContent="flex-start" alignItems="center" gap="10px" marginTop="20px">
                        <FilledButton text="Save" onClick={() => addUsersToOnboardingPipeline()} style={{ marginRight: "10px" }} />
                        {selectedEmployees.length > 0 && (
                            <OutlinedButton text="Clear All" onClick={() => {
                                setSelectedEmployees([]);
                                setIsAllEmployeesSelected(false);
                            }} />
                        )}
                    </Flexbox>
                </div>
            </Dialog>
        </Flexbox>
    );
};

export default EmployeePipelineManager;